<template>
  <v-dialog v-model="dialog" width="680" @click:outside="cancel" :persistent="isLoading">
    <v-card>
      <v-card-title>
        <h3><strong>{{ $t('storeMasterBulkDialog.label.title') }}</strong></h3>
      </v-card-title>
      <v-card-text>
        <div class="ml-4 mb-2" v-if="message" style="color: #2e7d32">
          {{ message }}
        </div>
        <div class="ml-4" v-if="errorMessage" style="color: red">
          {{ errorMessage }}
        </div>
        <!-- 行ごとの誤りは件数が多くなりうるので、スクロールできる枠にまとめる -->
        <div class="error_box ml-4 mt-1" v-if="errorList.length > 0">
          <div v-for="(data, idx) in errorList" :key="idx" style="color: red">
            {{ data.message }}
          </div>
        </div>

        <v-container>
          <v-row>
            <v-col cols="12">
              <div class="text-subtitle-2 mb-1">{{ $t('storeMasterBulkDialog.label.file') }}</div>
              <input
                ref="fileInput"
                type="file"
                :accept="acceptedFileTypes"
                style="display: none"
                @change="onFileSelected"
              />
              <v-btn variant="outlined" :disabled="isLoading" @click="openFilePicker">
                {{ $t('common.action.upload') }}
              </v-btn>
              <span class="ml-3">{{ fileName }}</span>
            </v-col>
          </v-row>
          <!-- 「内容を確認」の結果。反映する前に追加・変更を見せる -->
          <v-row v-if="preview">
            <v-col cols="12">
              <div class="text-subtitle-2 mb-1">{{ $t('storeMasterBulkDialog.label.previewTitle') }}</div>
              <div class="mb-2">
                {{ $t('storeMasterBulkDialog.label.summary', {
                  added: preview.addedList.length, changed: preview.changedList.length,
                  unchanged: preview.unchangedCount, kept: preview.keptCount }) }}
              </div>
              <div v-if="!hasChange" class="mb-2">{{ $t('storeMasterBulkDialog.label.noChange') }}</div>

              <template v-if="preview.addedList.length > 0">
                <div class="diff_head added">{{ $t('storeMasterBulkDialog.label.added') }}（{{ preview.addedList.length }}）</div>
                <div class="diff_box">
                  <table class="diff">
                    <thead><tr>
                      <th>{{ $t('storeMasterBulkDialog.label.colCode') }}</th>
                      <th>{{ $t('storeMasterBulkDialog.label.colName') }}</th>
                    </tr></thead>
                    <tbody>
                      <tr v-for="r in preview.addedList" :key="'a' + r.storeCode">
                        <td>{{ r.storeCode }}</td><td>{{ r.storeName }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </template>

              <template v-if="preview.changedList.length > 0">
                <div class="diff_head changed">{{ $t('storeMasterBulkDialog.label.changed') }}（{{ preview.changedList.length }}）</div>
                <div class="diff_box">
                  <table class="diff">
                    <thead><tr>
                      <th>{{ $t('storeMasterBulkDialog.label.colCode') }}</th>
                      <th>{{ $t('storeMasterBulkDialog.label.colBefore') }}</th>
                      <th>{{ $t('storeMasterBulkDialog.label.colAfter') }}</th>
                    </tr></thead>
                    <tbody>
                      <tr v-for="r in preview.changedList" :key="'c' + r.storeCode">
                        <td>{{ r.storeCode }}</td><td>{{ r.beforeName }}</td><td>{{ r.afterName }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </template>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <div class="format_box">
                <div class="text-subtitle-2 mb-1">{{ $t('storeMasterBulkDialog.label.formatTitle') }}</div>
                <ul class="pl-5">
                  <li>{{ $t('storeMasterBulkDialog.label.formatLine1') }}</li>
                  <li>{{ $t('storeMasterBulkDialog.label.formatLine2') }}</li>
                  <li>{{ $t('storeMasterBulkDialog.label.formatLine3') }}</li>
                  <li>{{ $t('storeMasterBulkDialog.label.formatLine4') }}</li>
                </ul>
                <div class="text-caption mt-2">{{ $t('storeMasterBulkDialog.label.sample') }}</div>
                <table class="sample">
                  <thead>
                    <tr>
                      <th>{{ $t('storeMasterBulkDialog.label.colCode') }}</th>
                      <th>{{ $t('storeMasterBulkDialog.label.colName') }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr><td>01234</td><td>無印良品 銀座</td></tr>
                    <tr><td>05678</td><td>無印良品 有楽町</td></tr>
                  </tbody>
                </table>
                <!-- CSV で作る場合の中身（S-40 フォーマット例を表示） -->
                <div class="text-caption mt-2">{{ $t('storeMasterBulkDialog.label.csvSample') }}</div>
                <pre class="csv_sample">店舗コード,店舗名
01234,無印良品 銀座
05678,無印良品 有楽町</pre>
              </div>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions class="pa-4">
        <v-spacer></v-spacer>
        <v-btn text @click="cancel" :disabled="isLoading">{{ $t('storeMasterBulkDialog.action.close') }}</v-btn>
        <v-btn
          v-if="!preview"
          color="indigo darken-1"
          dark
          :loading="isLoading"
          :disabled="!file"
          @click="previewFile"
        >{{ $t('storeMasterBulkDialog.action.preview') }}</v-btn>
        <template v-else>
          <v-btn variant="outlined" :disabled="isLoading" @click="reselect">
            {{ $t('storeMasterBulkDialog.action.reselect') }}
          </v-btn>
          <v-btn
            color="indigo darken-1"
            dark
            :loading="isLoading"
            :disabled="!hasChange"
            @click="importFile"
          >{{ $t('storeMasterBulkDialog.action.import') }}</v-btn>
        </template>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import api from "@/apis/staff";
import constant from "@/utils/constant";
import utils from "@/utils";
import { isAcceptedImportFile } from "@/utils/importFileType";

/**
 * 店舗一括登録（CSV・Excel取込。2026-10-21、CSV は 2026-10-27 から）
 *
 * 追加と店舗名の変更だけを行う。ファイルに無い店舗は消さない
 * （店舗は棚卸の結果から参照されるため、削除は画面で1件ずつ行う）。
 * 「内容を確認」で追加・変更を見せてから「この内容で反映」で登録する。
 */
export default {
  props: {
    handleCancel: {
      type: Function,
      required: true,
    },
    // 取り込みに成功したら、サーバーが返した最新の一覧を渡して呼ぶ
    handleImported: {
      type: Function,
      required: true,
    },
  },

  computed: {
    fileName() {
      return this.file ? this.file.name : "";
    },
    hasChange() {
      const p = this.preview;
      return !!p && (p.addedList.length + p.changedList.length) > 0;
    },
  },

  data() {
    return {
      dialog: true,
      isLoading: false,
      message: "",
      errorMessage: "",
      errorList: [],
      file: null,
      // 受け付けるファイルの種類（input の accept と、選んだときの確認に使う）
      acceptedFileTypes: ".csv,.xlsx",
      preview: null,
    };
  },

  methods: {
    clearMessages() {
      this.message = "";
      this.errorMessage = "";
      this.errorList = [];
    },
    cancel() {
      if (this.isLoading) return;
      this.handleCancel();
    },
    openFilePicker() {
      this.$refs.fileInput.click();
    },
    onFileSelected() {
      this.clearMessages();
      this.preview = null;
      const file = this.$refs.fileInput.files[0] ?? null;
      // 「すべてのファイル」に切り替えると別の種類も選べるので、選んだ時点で確かめて送らない
      if (file && !isAcceptedImportFile(file, this.acceptedFileTypes)) {
        this.resetFile();
        this.errorMessage = this.$t("common.message.selectCsvOrXlsxFile");
        return;
      }
      this.file = file;
    },
    reselect() {
      this.clearMessages();
      this.preview = null;
      this.resetFile();
    },
    // 同じファイルをもう一度選べるよう、入力の値も消す
    resetFile() {
      this.file = null;
      if (this.$refs.fileInput) this.$refs.fileInput.value = null;
    },
    send(preview) {
      const formData = new FormData();
      formData.append("file", this.file);
      if (preview) formData.append("preview", "true");
      return api.postFile("/store/master/bulk", formData);
    },
    // 登録はせず、今の登録内容との差分を受け取る
    previewFile() {
      if (!this.file) return;
      this.isLoading = true;
      this.clearMessages();
      this.send(true)
        .then((response) => {
          if (response.data.status == constant.RESPONSE_STATUS.SUCCESS) {
            this.preview = response.data.results;
            return;
          }
          this.errorMessage = response.data.errorMessage;
          this.errorList = response.data.errorList ?? [];
        })
        .catch((error) => {
          this.errorMessage = utils.getErrorMsg(error.response, this.$t);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    importFile() {
      if (!this.file) return;
      this.isLoading = true;
      this.clearMessages();
      this.send(false)
        .then((response) => {
          if (response.data.status == constant.RESPONSE_STATUS.SUCCESS) {
            const results = response.data.results ?? {};
            this.message = this.$t("storeMasterBulkDialog.message.success", {
              count: results.importedCount ?? 0,
              total: (results.storeList ?? []).length,
            });
            this.preview = null;
            this.resetFile();
            this.handleImported(results.storeList ?? []);
            return;
          }
          // 誤りがあったときは1件も登録されていない。直して選び直してもらう
          this.errorMessage = response.data.errorMessage;
          this.errorList = response.data.errorList ?? [];
        })
        .catch((error) => {
          this.errorMessage = utils.getErrorMsg(error.response, this.$t);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.error_box {
  max-height: 160px;
  overflow: auto;
  padding: 6px 10px;
  border: solid 1px #ef9a9a;
  border-radius: 4px;
  background: #fff5f5;
}
.diff_head {
  margin-top: 8px;
  font-size: 0.85rem;
  font-weight: bold;
  &.added { color: #2e7d32; }
  &.changed { color: #ef6c00; }
}
.diff_box {
  max-height: 180px;
  overflow: auto;
  border: solid 1px #e0e0e0;
  border-radius: 4px;
}
.diff {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.8rem;
  th,
  td {
    border-bottom: solid 1px #eeeeee;
    padding: 2px 8px;
    text-align: left;
  }
  th {
    position: sticky;
    top: 0;
    background: #f5f5f5;
  }
}
.format_box {
  padding: 10px 14px;
  border: solid 1px #e0e0e0;
  border-radius: 4px;
  background: #fafafa;
  font-size: 0.875rem;
}
.csv_sample {
  margin: 0;
  padding: 4px 8px;
  font-size: 13px;
  background: #f5f5f5;
  border: 1px solid #ddd;
  white-space: pre;
}
.sample {
  border-collapse: collapse;
  margin-top: 4px;
  th,
  td {
    border: solid 1px #bdbdbd;
    padding: 2px 14px;
    text-align: left;
  }
  th {
    background: #eeeeee;
  }
}
</style>
